/**
 * Yönlendirme: her bölüm kendi adresinde.
 *
 * ## Neden kütüphane yok
 *
 * Ürün sahibi: *"adam akilli panel olacak hepsinin sayfasi olacak."* Bugüne kadar her şey tek
 * uzun sayfadaydı ve menü çapayla (`#overview`) o sayfanın içinde kaydırıyordu.
 *
 * `react-router` eklemek düşünüldü ve **ölçüldü**: on adres, hiçbiri iç içe değil, hiçbirinde
 * parametre yok, veri yükleyicisi yok. Kütüphanenin çözdüğü sorunların hiçbiri burada yok.
 * Depoda bağımlılık eklememe eğilimi var (`web/package.json` bugün React ve Vite'ten başka
 * bağımlılık taşımıyor) ve bu ekranın ihtiyacı `history.pushState` ile `popstate`ten ibaret,
 * yani aşağıdaki kırk satır.
 *
 * ## Neden gerçek adres, kırık işareti (`#/satis`) değil
 *
 * Ölçüldü: `scripts/serve-web.mjs` bilinmeyen bir yol geldiğinde zaten `index.html` döndürüyor
 * (38. satır), Vite'in geliştirme ve önizleme sunucuları da öyle. Yani gerçek adres bu depoda
 * **sunucu değişikliği gerektirmiyor**, ve `/finans` bir operatörün kopyalayıp gönderebileceği
 * bir adres; `#/finans` değil.
 */

import { useCallback, useEffect, useState } from 'react';

/** Bir bölümün adresi. Değerler Türkçe çünkü adres de ekranın bir parçası. */
export const YOLLAR = {
  genelBakis: '/',
  musteriler: '/musteriler',
  turBilgi: '/tur-bilgi',
  /** Otel Teklifi (25.10.2026): Tur bilgi formunun otel karşılığı; otel numarasıyla bul, oda seç, PDF teklif. */
  otelTeklif: '/otel-teklif',
  /** Hızlı tatil teklifi (26.10.2026): çok otelli tek ekran, paralel fiyat, oda tipi seçimi, PDF ve mesaj taslağı. */
  hizliTeklif: '/hizli-teklif',
  satis: '/satis',
  operasyon: '/operasyon',
  /** Belge arşivi (02.11.2026): üretilen ve yüklenen bütün belgeler tek tabloda, sonradan indirilebilir. */
  belgeler: '/belgeler',
  finans: '/finans',
  pazarlama: '/pazarlama',
  /**
   * İK — beşinci bölüm (Ürün Sahibi, 30.08.2026).
   *
   * Adres `/ik`, `/insan-kaynaklari` değil: gezinme çubuğunda da ekranın başlığında da kısaltma
   * kullanılıyor ve adresin ekranla aynı kelimeyi taşıması gerekiyor. `/hr` de değil — buradaki
   * bütün adresler Türkçe ve bir istisna, kuralı okuyan için kuralı belirsiz yapar.
   */
  ik: '/ik',
  denetim: '/denetim',
  v3: '/web-sitesi',
  tedarikciler: '/tedarikciler',
  kullanicilar: '/kullanicilar',
  /**
   * Kod dağıtımı gerektirmeden değişen, sunucuya yazılan çalışma zamanı ayarları (bugün: istem
   * şablonları). `/ayarlar`dan ayrı ve bilerek: orası tarayıcıda saklanan kişisel tercihler,
   * burası bütün kullanıcıları etkileyen sistem yapılandırması. Gerekçenin tamamı
   * `Yapilandirma.tsx` başındaki notta.
   */
  yapilandirma: '/yapilandirma',
  /** Hesap menüsünün iki maddesi. Menüde yoklar, çünkü menü işin bölümlerini gösteriyor. */
  profil: '/profil',
  ayarlar: '/ayarlar',
} as const;

/**
 * İki dinamik adres: bir müşterinin ve bir siparişin kendi sayfası.
 *
 * `yonlendirme.ts`nin üstündeki not "on adres, hiçbiri parametre taşımıyor" diyordu ve o gün
 * doğruydu. Artık değil: bir müşteri satırına ya da bir sipariş koduna tıklamanın gerçek bir
 * adrese gitmesi gerekiyor (kopyalanabilir, gönderilebilir), sabit bir listeye değil. İkisi de
 * tek bir kimlik segmenti taşıyor, iç içe değil — kütüphane gerektirecek karmaşıklık hâlâ yok.
 */
const DINAMIK_ONEKLER = {
  musteriDetay: '/musteriler/',
  siparisDetay: '/siparisler/',
} as const;

/**
 * Bölüm anahtarı -> adres. `App.tsx`teki `BOLUM_YOLU` ile aynı eşleme, kasıtlı olarak ayrı bir
 * kopya: `App.tsx` zaten bu dosyadan okuyor, ters yönde bir içe aktarım dairesel olurdu.
 */
const BOLUM_YOLU: Record<string, Yol> = {
  SALES: YOLLAR.satis,
  OPERATIONS: YOLLAR.operasyon,
  FINANCE: YOLLAR.finans,
  MARKETING: YOLLAR.pazarlama,
  HR: YOLLAR.ik,
};

/**
 * Girişte düşülecek adres (CRM-A14, dashboard tasarımı §4.1 ve §5.1).
 *
 * Rolün gördüğü bölüm **tek** ise (Satış, Operasyon, Finans, Pazarlama ve İK'nın kendi
 * rolleri) o bölümün ekranına düşülür. ADMIN ve MANAGER beşini birden görüyor, dolayısıyla
 * "kendi" bölümleri yok ve genel bakışta kalırlar; hiçbir bölümü görmeyen (READ_ONLY) rol de
 * öyle. HR rolü yalnız İK'yı görüyor, yani `/ik` ekranına düşer.
 * `sections`, `GET /dashboard/sections`in `sections` alanı: tarayıcı rol adını kendi bilmiyor,
 * yalnız ucun döndürdüğü listeye bakıyor.
 */
export function girisYolu(sections: readonly { key: string }[]): Yol {
  if (sections.length !== 1) return YOLLAR.genelBakis;
  return BOLUM_YOLU[sections[0]!.key] ?? YOLLAR.genelBakis;
}

export function musteriYolu(id: string): string {
  return `${DINAMIK_ONEKLER.musteriDetay}${id}`;
}

export function siparisYolu(id: string): string {
  return `${DINAMIK_ONEKLER.siparisDetay}${id}`;
}

/** Bulunulan adres bir müşteri/sipariş detayıysa kimliği, değilse `null`. */
export function musteriKimligi(adres: string): string | null {
  return adres.startsWith(DINAMIK_ONEKLER.musteriDetay) ? adres.slice(DINAMIK_ONEKLER.musteriDetay.length) : null;
}

export function siparisKimligi(adres: string): string | null {
  return adres.startsWith(DINAMIK_ONEKLER.siparisDetay) ? adres.slice(DINAMIK_ONEKLER.siparisDetay.length) : null;
}

export type Yol = string;

const SABIT = Object.values(YOLLAR) as string[];

/** Adresin sonundaki eğik çizgi anlam taşımıyor: `/finans/` ile `/finans` aynı ekran. */
export function normalize(adres: string): Yol {
  const temiz = adres.replace(/\/+$/, '') || '/';
  if (SABIT.includes(temiz)) return temiz;
  for (const onek of Object.values(DINAMIK_ONEKLER)) {
    if (temiz.startsWith(onek) && temiz.length > onek.length) return temiz;
  }
  return YOLLAR.genelBakis;
}

/**
 * Bulunulan adres ve onu değiştirmenin yolu.
 *
 * `popstate` dinleniyor, yani tarayıcının **geri düğmesi** çalışıyor. `git()` aynı adrese
 * ikinci kez gitmiyor: aynı ekranın menüsüne tekrar tıklamak geçmişe boş bir kayıt eklemez,
 * yoksa geri düğmesi hiçbir şey yapmadan basılmaya devam ederdi.
 *
 * Bu kanca **birden çok yerde** çağrılıyor artık: kabuk (`App.tsx`), müşteri listesi, kuyruk
 * tabloları, detay sayfaları — her biri kendi satırından/bağlantısından `git()` çağırabilsin
 * diye. `window.history.pushState` **kendi başına `popstate` fırlatmaz** (yalnız geri/ileri
 * tetikler), yani bir çağrı yeri `pushState` yapınca öteki çağrı yerlerinin `yol`u hiç
 * güncellenmiyordu — kabuk eski sayfada kalıyor, adres çubuğu değişse bile. Çözüm `pushState`den
 * sonra sentetik bir `popstate` fırlatmak: bütün `useYol()` örnekleri zaten onu dinliyor, tek
 * kaynaktan (adres çubuğu) senkron kalıyorlar.
 */
export function useYol(): { yol: Yol; git: (hedef: Yol) => void } {
  const [yol, setYol] = useState<Yol>(() => normalize(window.location.pathname));

  useEffect(() => {
    const dinle = () => setYol(normalize(window.location.pathname));
    window.addEventListener('popstate', dinle);
    return () => window.removeEventListener('popstate', dinle);
  }, []);

  const git = useCallback((hedef: Yol) => {
    if (normalize(window.location.pathname) === hedef) return;
    window.history.pushState(null, '', hedef);
    window.dispatchEvent(new PopStateEvent('popstate'));
    // Yeni bir sayfa açılıyor, eskisinin kaydırma yeri taşınmıyor.
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return { yol, git };
}

/**
 * Menü bağlantısı için tıklama işleyicisi.
 *
 * Bağlantı gerçek bir `<a href="/finans">` olarak kalıyor: yeni sekmede açmak, adresi kopyalamak
 * ve ekran okuyucunun onu bağlantı olarak okuması buna bağlı. Yalnız **düz sol tıklama**
 * yakalanıyor; `Ctrl`, `Cmd`, `Shift` ya da orta tuş tarayıcıya bırakılıyor.
 */
export function baglantiTiklamasi(git: (hedef: Yol) => void, hedef: Yol) {
  return (olay: React.MouseEvent<HTMLAnchorElement>) => {
    if (olay.defaultPrevented || olay.button !== 0) return;
    if (olay.metaKey || olay.ctrlKey || olay.shiftKey || olay.altKey) return;
    olay.preventDefault();
    git(hedef);
  };
}
